import React from 'react';
import sharedStyles from './ManagerShared.module.css';
import styles from './ManagerStreakForm.module.css';

export default function ManagerStreakForm({ streak, frameClassName, compact = false, hideTitle = false }) {
    if (!streak || !streak.results || streak.results.length === 0) return null;

    const results = streak.results;
    const count = results.length || 1;

    // Desktop SVG Config
    const N = results.length;
    const W = 500;
    const H = 160;
    const BOX = 36;

    const yForResultDesktop = (r) => r === 'W' ? 30 : r === 'D' ? 85 : 140;
    const colorForResultDesktop = (r) => r === 'W' ? '#00A854' : r === 'L' ? '#E53935' : '#504F59';
    const textColorForResultDesktop = (r) => r === 'W' ? '#000000' : r === 'L' ? '#ffffff' : '#ffffff';

    const colW = W / N;

    const desktopPoints = results.map((result, i) => {
        const x = (i + 0.5) * colW;
        const y = yForResultDesktop(result);
        return { x, y, result };
    });

    const polylineStr = desktopPoints.map(p => `${p.x},${p.y}`).join(' ');

    // Mobile Bezier Config (matching ClubStreakForm layout)
    const getYPercentMobile = (res) => {
        if (res === 'W') return 10;
        if (res === 'D') return 40;
        return 70;
    };

    const stepMobile = 335 / count;
    const startXMobile = stepMobile / 2;
    const markerMiddleOffsetMobile = 9;

    const mobilePoints = results.map((r, i) => ({
        x: startXMobile + (i * stepMobile),
        y: getYPercentMobile(r) + markerMiddleOffsetMobile
    }));

    const generateWavePath = (pts) => {
        if (pts.length < 2) return '';
        let d = `M ${pts[0].x},${pts[0].y}`;
        
        for (let i = 0; i < pts.length - 1; i++) {
            const curr = pts[i];
            const next = pts[i + 1];
            const cp1x = curr.x + (next.x - curr.x) / 2;
            const cp2x = next.x - (next.x - curr.x) / 2;
            d += ` C ${cp1x},${curr.y} ${cp2x},${next.y} ${next.x},${next.y}`;
        }
        return d;
    };

    const wavePathMobile = generateWavePath(mobilePoints);

    return (
        <section className={sharedStyles.section}>
            {!hideTitle && <h2 className={sharedStyles.sectionTitle}>The Streak</h2>}
            
            {/* Desktop Version */}
            <div className={`${styles.formPanel} ${styles.desktopOnlyChart} ${frameClassName || ''}`}>
                <div className={styles.formChart}>
                    <svg
                        viewBox={`0 0 ${W} ${H + 32}`}
                        preserveAspectRatio="xMidYMid meet"
                        className={styles.chartSvg}
                    >

                        {/* Vertical guide lines with bottom dots */}
                        {desktopPoints.map((p, i) => (
                            <g key={`vline-group-${i}`}>
                                <line
                                    x1={p.x}
                                    y1={170}
                                    x2={p.x}
                                    y2={p.y}
                                    stroke="rgba(255, 255, 255, 0.03)"
                                    strokeWidth="1"
                                />
                                <circle
                                    cx={p.x}
                                    cy={170}
                                    r="2.5"
                                    fill="rgba(255, 255, 255, 0.05)"
                                />
                            </g>
                        ))}

                        {/* Dashed connecting polyline */}
                        <polyline
                            points={polylineStr}
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.15)"
                            strokeWidth="4"
                            strokeDasharray="8 6"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />

                        {/* Result boxes */}
                        {desktopPoints.map((p, i) => (
                            <g key={`node-${i}`}>
                                <rect
                                    x={p.x - BOX / 2}
                                    y={p.y - BOX / 2}
                                    width={BOX}
                                    height={BOX}
                                    rx="5"
                                    ry="5"
                                    fill={colorForResultDesktop(p.result)}
                                />
                                <text
                                    x={p.x}
                                    y={p.y + 4.5}
                                    textAnchor="middle"
                                    fill={textColorForResultDesktop(p.result)}
                                    fontSize="15"
                                    fontWeight="900"
                                    fontFamily="Inter, sans-serif"
                                >
                                    {p.result}
                                </text>
                            </g>
                        ))}

                        {/* Axis numbers */}
                        {desktopPoints.map((p, i) => (
                            <text
                                key={`axis-${i}`}
                                x={p.x}
                                y={H + 26}
                                textAnchor="middle"
                                fill="rgba(255,255,255,0.50)"
                                fontSize="12"
                                fontFamily="Inter, sans-serif"
                                fontWeight="400"
                                style={{ letterSpacing: '-0.48px' }}
                            >
                                {i + 1}
                            </text>
                        ))}
                    </svg>
                </div>
            </div>

            {/* Mobile Version (ClubStreakForm Layout style) */}
            <div className={`${styles.formPanel} ${styles.mobileOnlyChart} ${frameClassName || ''}`}>
                <div className={styles.formChartMobile}>
                    <svg viewBox="0 0 335 100" preserveAspectRatio="none" className={styles.trendSvgMobile}>
                        {results.map((_, i) => {
                            const x = startXMobile + (i * stepMobile);
                            return (
                                <React.Fragment key={i}>
                                    <line x1={x} y1="0" x2={x} y2="100" className={styles.guideLineMobile} />
                                    <circle cx={x} cy="100" r="2.5" className={styles.dotMobile} />
                                </React.Fragment>
                            );
                        })}
                        <path d={wavePathMobile} className={styles.trendLineMobile} />
                    </svg>

                    <div
                        className={styles.resultMarkersMobile}
                        style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
                    >
                        {results.map((result, index) => (
                            <div
                                className={styles.markerWrapperMobile}
                                key={index}
                                style={{
                                    position: 'absolute',
                                    left: `${(index * 100 / count)}%`,
                                    width: `${100 / count}%`,
                                    top: `${getYPercentMobile(result)}%`
                                }}
                            >
                                <span className={styles[`result${result}`]}>
                                    {result}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div
                        className={styles.axisLabelsMobile}
                        style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
                    >
                        {results.map((_, i) => <span key={i}>{i + 1}</span>)}
                    </div>
                </div>
            </div>
        </section>
    );
}
